import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { GroovePullquoteDirective } from '@groove/angular';
import {
  ImageGalleryComponent,
  type GalleryImage,
} from '../../../../content/image-gallery/image-gallery.component';
import { SEOService } from '../../../../seo.service';
import { buildBioPageSchemas } from './bio-jsonld';

@Component({
  selector: 'app-bio-content',
  imports: [
    NgOptimizedImage,
    RouterLink,
    ImageGalleryComponent,
    GroovePullquoteDirective,
  ],
  templateUrl: './bio-content.component.html',
  styleUrl: './bio-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BioContentComponent implements OnDestroy {
  private seo = inject(SEOService);

  constructor() {
    // No route param to react to, so a plain constructor call is enough —
    // this page's JSON-LD never changes after mount. Sets the ProfilePage
    // schema that used to live in app.routes.ts's static data.seo.jsonLd
    // via the eagerly-imported seo-profile.ts; see bio-jsonld.ts for why
    // this moved (keeps the Person node out of the eager route-data bundle).
    this.seo.setRouteSchemas(buildBioPageSchemas());
  }

  ngOnDestroy(): void {
    this.seo.clearRouteSchemas();
  }

  // The 12 curated performance photos in display order. Thumbnails use a
  // uniform 1:1 aspect ratio so the grid reads as an even mosaic. Alt text
  // matches the original hand-rolled grid; do not alter without reviewing
  // the page.
  protected readonly galleryImages: GalleryImage[] = [
    {
      src: '/images/bio-gallery/beret-daylight.webp',
      alt: 'Russ Rodgers in a beret playing his bass in warm daylight',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/stage-red.webp',
      alt: 'Russ Rodgers on stage playing his Ken Smith bass under red lighting',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/purple-closeup.webp',
      alt: 'Russ Rodgers playing bass under dramatic purple stage lighting',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/with-drummer.webp',
      alt: 'Russ Rodgers playing bass alongside a drummer during a live set',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/outdoor-daytime.webp',
      alt: 'Russ Rodgers playing bass at an outdoor daytime performance',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/bw-closeup.webp',
      alt: 'Black-and-white close-up of Russ Rodgers playing bass',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/blue-stage.webp',
      alt: 'Russ Rodgers playing bass on a blue-lit stage',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/candid-brick.webp',
      alt: 'Russ Rodgers between numbers, bass in hand, against a brick wall',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/street-gig.webp',
      alt: 'Russ Rodgers playing bass at a street-side bar gig at night',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/warm-wood.webp',
      alt: 'Russ Rodgers playing bass on a warm wood-panelled stage',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/tiki-red.webp',
      alt: 'Russ Rodgers in a Hawaiian shirt playing bass at a red-lit bar',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
    {
      src: '/images/bio-gallery/festival.webp',
      alt: 'Russ Rodgers performing bass outdoors at a daytime festival',
      aspectRatio: '1 / 1',
      sizes: '(max-width: 600px) 45vw, (max-width: 900px) 30vw, 22vw',
    },
  ];
}
